import React, { useEffect, useMemo, useState } from "react";
import { Container } from "reactstrap";
import { toast } from "react-toastify";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
} from "../../components/shared/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/shared/alertDialog";
import {
  createCoupon,
  getCoupons,
  updateCoupon,
  deleteCoupon,
} from "../../apis/adminCoupon";
import { X, Edit, Trash2 } from "lucide-react";
import { z } from "zod";
import { AxiosError } from "axios";

// Validation Schema
const couponSchema = z.object({
  code: z.string().min(1, "Coupon code is required"),
  discountType: z.enum(["percentage", "fixed"], {
    errorMap: () => ({ message: "Select a valid discount type" }),
  }),
  discountValue: z.number().min(0, "Value must be positive"),
  minBookingAmount: z.number().min(0).optional(),
  startDate: z.string().min(1, "Start date is required"),
  endDate: z.string().min(1, "End date is required"),
  usageLimit: z
    .number()
    .min(1, "Usage limit must be at least 1")
    .max(100, "Usage limit cannot exceed 100"),
  isActive: z.boolean(),
});

const CouponsList = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(false);

  // Dialog State
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogTitle, setDialogTitle] = useState("");
  const [currentCouponId, setCurrentCouponId] = useState(null);

  // Form State
  const [formData, setFormData] = useState({
    code: "",
    discountType: "percentage",
    discountValue: "",
    minBookingAmount: 0,
    startDate: "",
    endDate: "",
    usageLimit: 10,
    isActive: true,
  });
  const [errors, setErrors] = useState({});
  const [usageLimitError, setUsageLimitError] = useState("");
  const [discountValueError, setDiscountValueError] = useState("");

  // Alert Dialog State
  const [openAlertDialog, setOpenAlertDialog] = useState(false);
  const [couponToDelete, setCouponToDelete] = useState(null);

  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    setLoading(true);
    try {
      const response = await getCoupons();
      let data = [];

      if (Array.isArray(response)) {
        data = response;
      } else if (response && Array.isArray(response.coupons)) {
        data = response.coupons;
      } else if (response && Array.isArray(response.data)) {
        data = response.data;
      } else if (
        response &&
        response.data &&
        Array.isArray(response.data.coupons)
      ) {
        data = response.data.coupons;
      }

      setCoupons(data);
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch coupons");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Real-time validation for usage limit
    if (name === "usageLimit") {
      const numValue = Number(value);
      if (value === "") {
        setUsageLimitError("");
      } else if (isNaN(numValue)) {
        setUsageLimitError("Usage limit must be a number");
      } else if (numValue < 1) {
        setUsageLimitError("Usage limit must be at least 1");
      } else if (numValue > 100) {
        setUsageLimitError("Usage limit cannot exceed 100");
      } else {
        setUsageLimitError("");
      }
    }

    // Real-time validation for discount value
    if (name === "discountValue") {
      const numValue = Number(value);
      if (value === "") {
        setDiscountValueError("");
      } else if (isNaN(numValue)) {
        setDiscountValueError("Discount value must be a number");
      } else if (numValue <= 0) {
        setDiscountValueError("Discount value must be greater than 0");
      } else if (formData.discountType === "percentage" && numValue > 50) {
        setDiscountValueError("Percentage value cannot exceed 50%");
      } else if (formData.discountType === "fixed" && editingCoupon) {
        const maxAllowed = getMaxFixedDiscountPrice(editingCoupon);
        if (maxAllowed > 0 && numValue > maxAllowed) {
          setDiscountValueError(
            `Fixed discount cannot exceed ${maxAllowed} (50% of lowest property price)`,
          );
        } else {
          setDiscountValueError("");
        }
      } else {
        setDiscountValueError("");
      }
    }

    setFormData((prev) => {
      const updated = {
        ...prev,
        [name]:
          name === "discountValue" ||
          name === "minBookingAmount" ||
          name === "usageLimit"
            ? Number(value)
            : value,
      };

      // Re-validate discount value when discount type changes
      if (name === "discountType" && prev.discountValue) {
        const numValue = Number(prev.discountValue);
        if (!isNaN(numValue) && numValue > 0) {
          if (value === "percentage" && numValue > 50) {
            setDiscountValueError("Percentage value cannot exceed 50%");
          } else if (value === "fixed" && editingCoupon) {
            const maxAllowed = getMaxFixedDiscountPrice(editingCoupon);
            if (maxAllowed > 0 && numValue > maxAllowed) {
              setDiscountValueError(
                `Fixed discount cannot exceed ${maxAllowed} (50% of lowest property price)`,
              );
            } else {
              setDiscountValueError("");
            }
          } else {
            setDiscountValueError("");
          }
        }
      }

      return updated;
    });
  };

  const handleToggleActive = () => {
    setFormData((prev) => ({ ...prev, isActive: !prev.isActive }));
  };

  // const resetForm = () => {
  //   setFormData({
  //     code: "",
  //     discountType: "percentage",
  //     discountValue: "",
  //     minBookingAmount: 0,
  //     startDate: "",
  //     endDate: "",
  //     usageLimit: 10,
  //     isActive: true,
  //   });
  //   setErrors({});
  //   setCurrentCouponId(null);
  // };

  // const handleOpenCreate = () => {
  //   setDialogTitle("Create Coupon");
  //   resetForm();
  //   setOpenDialog(true);
  // };

  // Compute max allowed fixed discount from coupon's properties (50% of lowest basePrice)
  const getMaxFixedDiscountPrice = (coupon) => {
    const props = coupon?.properties || [];
    if (props.length === 0) return 0;

    const prices = props
      .map((p) => p.basePrice)
      .filter((price) => typeof price === "number" && price > 0);

    if (prices.length === 0) return 0;

    const lowestPrice = Math.min(...prices);
    return Math.floor(lowestPrice * 0.5);
  };

  const [editingCoupon, setEditingCoupon] = useState(null);

  const handleOpenEdit = (coupon) => {
    setDialogTitle("Edit Coupon");
    setCurrentCouponId(coupon._id);
    setEditingCoupon(coupon);
    setFormData({
      code: coupon.code,
      discountType: coupon.discountType,
      discountValue: coupon.discountValue,
      minBookingAmount: coupon.minBookingAmount || 0,
      startDate: coupon.startDate ? coupon.startDate.split("T")[0] : "",
      endDate: coupon.endDate ? coupon.endDate.split("T")[0] : "",
      usageLimit: coupon.usageLimit || 10,
      isActive: coupon.isActive !== undefined ? coupon.isActive : true,
    });
    setErrors({});
    setUsageLimitError("");
    setDiscountValueError("");
    setOpenDialog(true);
  };

  const handleSubmit = async () => {
    try {
      // Clear previous errors
      setErrors({});

      // Validate required fields
      if (!formData.code || !formData.startDate || !formData.endDate) {
        toast.error("Please fill all required fields");
        return;
      }

      // Validate usage limit
      if (usageLimitError) {
        toast.error(usageLimitError);
        return;
      }

      if (!formData.usageLimit) {
        toast.error("Usage limit is required");
        return;
      }

      // Validate discount value error
      if (discountValueError) {
        toast.error(discountValueError);
        return;
      }

      // Validate discount value
      if (!formData.discountValue || formData.discountValue <= 0) {
        toast.error("Discount value must be greater than 0");
        return;
      }

      // Validate percentage max 50%
      if (
        formData.discountType === "percentage" &&
        formData.discountValue > 50
      ) {
        toast.error("Percentage value cannot exceed 50%");
        return;
      }

      // Validate fixed discount against max allowed (50% of lowest property price)
      if (formData.discountType === "fixed" && editingCoupon) {
        const maxAllowed = getMaxFixedDiscountPrice(editingCoupon);
        if (maxAllowed > 0 && formData.discountValue > maxAllowed) {
          toast.error(
            `Fixed discount cannot exceed ${maxAllowed} (50% of lowest property price)`,
          );
          return;
        }
      }

      // Validate dates
      const startDateObj = new Date(formData.startDate);
      const endDateObj = new Date(formData.endDate);

      if (isNaN(startDateObj.getTime())) {
        toast.error("Invalid start date");
        return;
      }

      if (isNaN(endDateObj.getTime())) {
        toast.error("Invalid end date");
        return;
      }

      // Validate start date is before end date
      if (startDateObj >= endDateObj) {
        toast.error("Start date must be before end date");
        return;
      }

      const validData = couponSchema.parse(formData);

      if (currentCouponId) {
        await updateCoupon(currentCouponId, validData);
        toast.success("Coupon updated successfully");
      } else {
        const payload = { ...validData, property: null };
        await createCoupon(payload);
        toast.success("Coupon created successfully");
      }

      setOpenDialog(false);
      fetchCoupons();
    } catch (err) {
      if (err instanceof z.ZodError) {
        const fieldErrors = {};
        err.errors.forEach((error) => {
          fieldErrors[error.path[0]] = error.message;
        });
        setErrors(fieldErrors);
      } else if (err instanceof AxiosError && err.response?.data?.message) {
        toast.error(err.response.data.message);
      } else {
        toast.error("An error occurred");
      }
    }
  };

  const handleDelete = async () => {
    if (!couponToDelete) return;
    try {
      await deleteCoupon(couponToDelete);
      toast.success("Coupon deleted successfully");
      setOpenAlertDialog(false);
      fetchCoupons();
    } catch (error) {
      toast.error("Failed to delete coupon");
    }
  };

  // Helper function to determine coupon status based on current date
  const getCouponStatus = (startDate, endDate) => {
    const now = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);

    if (now < start) {
      return { status: "upcoming", label: "Upcoming" };
    } else if (now > end) {
      return { status: "expired", label: "Expired" };
    } else {
      return { status: "active", label: "Active" };
    }
  };

  // Helper function to format dates with locale support
  const formatDateRange = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);

    const options = {
      day: "2-digit",
      month: "short",
      year: "numeric",
    };

    const startStr = start.toLocaleDateString("en-US", options);
    const endStr = end.toLocaleDateString("en-US", options);

    return `${startStr} - ${endStr}`;
  };

  const columns = useMemo(
    () => ["Code", "Type", "Value", "Period", "Usage", "Status", "Actions"],
    [],
  );

  // Reusable input class — copied exactly from ManageAdminUsers
  const inputClass =
    "w-full px-4 py-3 rounded-sm dark:bg-darkBg1 dark:text-[#c7d8eb] border border-[rgba(0,0,0,0.6)] dark:border-[rgba(255,255,255,0.6)]";

  return (
    <React.Fragment>
      <div className="page-content dark:bg-darkBg1 dark:text-[#c7d8eb]">
        <Container fluid>
          <div className="p-6 flex flex-col gap-6">
            <div className="flex justify-between items-center">
              <h1 className="text-2xl dark:text-[#c7d8eb]">
                Coupon Management
              </h1>
              {/* <button
                onClick={handleOpenCreate}
                className="w-fit rounded-md font-bold text-lg px-4 py-2 bg-primaryColor text-white"
              >
                Add Coupon
              </button> */}
            </div>

            <div className="lg:flex flex-col lg:border lg:border-b-0 border-gray-400 dark:border-white rounded-md">
              <div className="overflow-x-auto">
                {/* Desktop Header */}
                <div className="hidden lg:flex w-full bg-gray-100 dark:bg-gray-800">
                  {columns.map((column, index) => (
                    <div
                      key={`column-${index}`}
                      className="flex-1 min-w-[120px] p-4 font-semibold text-center border-b border-gray-300 dark:border-gray-600"
                    >
                      {column}
                    </div>
                  ))}
                </div>

                {/* List */}
                {!Array.isArray(coupons) || coupons.length === 0 ? (
                  <div className="text-center p-4">No coupons found.</div>
                ) : (
                  coupons.map((coupon, index) => (
                    <div
                      key={coupon._id || index}
                      className="flex flex-col lg:flex-row border-b border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                    >
                      {/* Desktop Row */}
                      <div className="hidden lg:flex w-full items-center">
                        <div className="flex-1 min-w-[120px] p-3 text-center">
                          {coupon.code}
                        </div>
                        <div className="flex-1 min-w-[120px] p-3 text-center capitalize">
                          {coupon.discountType}
                        </div>
                        <div className="flex-1 min-w-[120px] p-3 text-center">
                          {coupon.discountValue}
                          {coupon.discountType === "percentage" ? "%" : ""}
                        </div>
                        <div className="flex-1 min-w-[120px] p-3 text-center">
                          {formatDateRange(coupon.startDate, coupon.endDate)}
                        </div>
                        <div className="flex-1 min-w-[120px] p-3 text-center">
                          {coupon.usedCount} / {coupon.usageLimit}
                        </div>
                        {/* Status Badge */}
                        <div className="flex-1 min-w-[120px] p-3 text-center">
                          {(() => {
                            const { status, label } = getCouponStatus(
                              coupon.startDate,
                              coupon.endDate,
                            );
                            const statusStyles = {
                              active:
                                "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300",
                              upcoming:
                                "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300",
                              expired:
                                "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300",
                            };
                            return (
                              <span
                                className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${statusStyles[status]}`}
                              >
                                {label}
                              </span>
                            );
                          })()}
                        </div>
                        <div className="flex-1 min-w-[120px] p-3 text-center flex justify-center gap-3">
                          <Edit
                            size={18}
                            className="cursor-pointer text-blue-500 hover:text-blue-700"
                            onClick={() => handleOpenEdit(coupon)}
                          />
                          <Trash2
                            size={18}
                            className="cursor-pointer text-red-500 hover:text-red-700"
                            onClick={() => {
                              setCouponToDelete(coupon._id);
                              setOpenAlertDialog(true);
                            }}
                          />
                        </div>
                      </div>

                      {/* Mobile Card View */}
                      <div className="lg:hidden p-4 space-y-2">
                        <div className="flex justify-between">
                          <span className="font-bold">Code:</span>
                          <span>{coupon.code}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-bold">Type:</span>
                          <span className="capitalize">
                            {coupon.discountType}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-bold">Value:</span>
                          <span>
                            {coupon.discountValue}
                            {coupon.discountType === "percentage" ? "%" : ""}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-bold">Period:</span>
                          <span>
                            {formatDateRange(coupon.startDate, coupon.endDate)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="font-bold">Status:</span>
                          {(() => {
                            const { status, label } = getCouponStatus(
                              coupon.startDate,
                              coupon.endDate,
                            );
                            const statusStyles = {
                              active:
                                "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300",
                              upcoming:
                                "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300",
                              expired:
                                "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300",
                            };
                            return (
                              <span
                                className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${statusStyles[status]}`}
                              >
                                {label}
                              </span>
                            );
                          })()}
                        </div>
                        <div className="flex justify-start gap-4 mt-2">
                          <button
                            onClick={() => handleOpenEdit(coupon)}
                            className="text-blue-500 font-semibold"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => {
                              setCouponToDelete(coupon._id);
                              setOpenAlertDialog(true);
                            }}
                            className="text-red-500 font-semibold"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </Container>

        {/* Create/Edit Dialog */}
        <Dialog open={openDialog} onOpenChange={setOpenDialog}>
          <DialogContent className="max-w-screen w-[calc(100vw-20px)] lg:w-[600px] max-h-[90vh] overflow-auto bg-gray-200 text-gray-900 dark:bg-[#171d37] dark:text-[#c7d8eb]">
            <DialogHeader className="flex flex-row justify-between items-center">
              <h2 className="text-xl font-bold dark:text-[#c7d8eb]">
                {dialogTitle}
              </h2>
              <X
                className="cursor-pointer dark:text-[#c7d8eb]"
                onClick={() => setOpenDialog(false)}
              />
            </DialogHeader>
            <hr className="border-t-[rgba(0,0,0,0.6)] dark:border-t-[rgba(255,255,255,0.6)] -mt-3" />

            <div className="space-y-4 p-2">
              {/* Code */}
              <div className="flex flex-col gap-1">
                <label className="font-semibold dark:text-[#c7d8eb]">
                  Code
                </label>
                <input
                  name="code"
                  value={formData.code}
                  onChange={handleInputChange}
                  placeholder="e.g. SUMMER2024"
                  className={inputClass}
                />
                {errors.code && (
                  <p className="text-red-500 text-sm">{errors.code}</p>
                )}
              </div>

              {/* Discount Type & Value */}
              <div className="flex gap-4">
                <div className="flex flex-col gap-1 w-1/2">
                  <label className="font-semibold dark:text-[#c7d8eb]">
                    Type
                  </label>
                  <select
                    name="discountType"
                    value={formData.discountType}
                    onChange={handleInputChange}
                    className={inputClass}
                  >
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed Amount</option>
                  </select>
                </div>
                <div className="flex flex-col gap-1 w-1/2">
                  <label className="font-semibold dark:text-[#c7d8eb]">
                    Value
                  </label>
                  <input
                    type="number"
                    name="discountValue"
                    value={formData.discountValue}
                    onChange={handleInputChange}
                    className={inputClass}
                  />
                  {discountValueError && (
                    <p className="text-red-500 text-sm">{discountValueError}</p>
                  )}
                  {errors.discountValue && (
                    <p className="text-red-500 text-sm">
                      {errors.discountValue}
                    </p>
                  )}
                  {formData.discountType === "fixed" &&
                    editingCoupon &&
                    (() => {
                      const maxAllowed =
                        getMaxFixedDiscountPrice(editingCoupon);
                      return maxAllowed > 0 ? (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Max allowed: {maxAllowed} (50% of lowest property
                          price)
                        </p>
                      ) : null;
                    })()}
                </div>
              </div>

              {/* Start & End Dates */}
              <div className="flex gap-4">
                <div className="flex flex-col gap-1 w-1/2">
                  <label className="font-semibold dark:text-[#c7d8eb]">
                    Start Date
                  </label>
                  <input
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleInputChange}
                    className={inputClass}
                  />
                  {errors.startDate && (
                    <p className="text-red-500 text-sm">{errors.startDate}</p>
                  )}
                </div>
                <div className="flex flex-col gap-1 w-1/2">
                  <label className="font-semibold dark:text-[#c7d8eb]">
                    End Date
                  </label>
                  <input
                    type="date"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleInputChange}
                    className={inputClass}
                  />
                  {errors.endDate && (
                    <p className="text-red-500 text-sm">{errors.endDate}</p>
                  )}
                </div>
              </div>
              {formData.startDate &&
                formData.endDate &&
                new Date(formData.startDate) >= new Date(formData.endDate) && (
                  <p className="text-red-500 text-sm">
                    Start date must be before end date
                  </p>
                )}
              {formData.discountType === "percentage" &&
                formData.discountValue > 50 && (
                  <p className="text-red-500 text-sm">
                    Percentage value cannot exceed 50%
                  </p>
                )}

              {/* Min Booking & Usage Limit */}
              <div className="flex gap-4">
                <div className="flex flex-col gap-1 w-1/2">
                  <label className="font-semibold dark:text-[#c7d8eb]">
                    Min Booking Amt
                  </label>
                  <input
                    type="number"
                    name="minBookingAmount"
                    value={formData.minBookingAmount}
                    onChange={handleInputChange}
                    className={inputClass}
                  />
                </div>
                <div className="flex flex-col gap-1 w-1/2">
                  <label className="font-semibold dark:text-[#c7d8eb]">
                    Usage Limit
                  </label>
                  <input
                    type="number"
                    name="usageLimit"
                    value={formData.usageLimit}
                    onChange={handleInputChange}
                    className={inputClass}
                    placeholder="1-100"
                  />
                  {usageLimitError && (
                    <p className="text-red-500 text-sm">{usageLimitError}</p>
                  )}
                </div>
              </div>

              {/* isActive Toggle */}
              <div className="flex items-center justify-between">
                <label className="font-semibold dark:text-[#c7d8eb]">
                  Active Status
                </label>
                <button
                  type="button"
                  onClick={handleToggleActive}
                  className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors duration-300 focus:outline-none ${
                    formData.isActive
                      ? "bg-green-500"
                      : "bg-gray-400 dark:bg-gray-600"
                  }`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform duration-300 ${
                      formData.isActive ? "translate-x-8" : "translate-x-1"
                    }`}
                  />
                </button>
              </div>
            </div>

            <DialogFooter className="mt-4">
              <button
                onClick={handleSubmit}
                className="bg-primaryColor text-white px-4 py-2 rounded shadow hover:bg-opacity-90"
              >
                Save
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={openAlertDialog} onOpenChange={setOpenAlertDialog}>
          <AlertDialogContent className="bg-gray-200 text-gray-900 dark:bg-[#171d37] dark:text-[#c7d8eb]">
            <AlertDialogHeader>
              <AlertDialogTitle>Are you sure?</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone. This will permanently delete the
                coupon.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setOpenAlertDialog(false)}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-red-600 text-white"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </React.Fragment>
  );
};

export default CouponsList;
